import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Eye,
  Pen,
  ChevronRight,
  FileSignature,
  AlertCircle
} from 'lucide-react';

const MySignaturesPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/signature-requests');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching signature requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      viewed: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Visto', icon: Eye },
      signed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Firmado', icon: CheckCircle2 },
      completed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Completado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      expired: { className: 'bg-muted text-muted-foreground border-muted', label: 'Expirado', icon: Clock },
      cancelled: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cancelado', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={`${style.className} gap-1`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </Badge>
    );
  };

  const pendingRequests = requests.filter(r => ['pending', 'viewed'].includes(r.status));
  const completedRequests = requests.filter(r => ['signed', 'completed'].includes(r.status));
  const otherRequests = requests.filter(r => ['rejected', 'expired', 'cancelled'].includes(r.status));

  const renderRequestCard = (request) => (
    <Card key={request.request_id} className="card-dashboard hover:border-primary/30 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium truncate">{request.template_name}</h3>
              <p className="text-sm text-muted-foreground truncate">
                Ref: {request.document_reference}
              </p>
              {request.opportunity_title && (
                <p className="text-sm text-muted-foreground truncate mt-1">
                  Proyecto: {request.opportunity_title}
                </p>
              )}
              <div className="flex items-center gap-3 mt-2">
                {getStatusBadge(request.status)}
                <span className="text-xs text-muted-foreground">
                  {new Date(request.created_at).toLocaleDateString('es-MX')}
                </span>
              </div>
            </div>
          </div>
          <Link to={`/app/signatures/${request.request_id}`}>
            <Button 
              size="sm" 
              className={['pending', 'viewed'].includes(request.status) ? 'btn-primary' : 'btn-outline'}
            >
              {['pending', 'viewed'].includes(request.status) ? (
                <>
                  <Pen className="h-3 w-3 mr-1" />
                  Firmar
                </>
              ) : (
                <>
                  <Eye className="h-3 w-3 mr-1" />
                  Ver
                </>
              )}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  const renderEmptyState = (message) => (
    <Card className="card-dashboard">
      <CardContent className="p-8 text-center">
        <FileSignature className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Mis Documentos</h1>
        <p className="text-muted-foreground mt-1">
          Documentos que requieren su firma o revisión
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-dashboard">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{pendingRequests.length}</p>
              <p className="text-sm text-muted-foreground">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{completedRequests.length}</p>
              <p className="text-sm text-muted-foreground">Firmados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{requests.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Alert */}
      {pendingRequests.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-600">
              Tiene <span className="font-semibold">{pendingRequests.length} documento(s)</span> pendiente(s) de firma. 
              Por favor revíselos a la brevedad.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="gap-2">
            <Clock className="h-4 w-4" />
            Pendientes ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Firmados ({completedRequests.length})
          </TabsTrigger>
          <TabsTrigger value="other" className="gap-2">
            <XCircle className="h-4 w-4" />
            Otros ({otherRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 mt-4">
          {pendingRequests.length === 0 
            ? renderEmptyState('No tiene documentos pendientes de firma.')
            : pendingRequests.map(renderRequestCard)
          }
        </TabsContent>

        <TabsContent value="completed" className="space-y-3 mt-4">
          {completedRequests.length === 0 
            ? renderEmptyState('No tiene documentos firmados.')
            : completedRequests.map(renderRequestCard)
          }
        </TabsContent>

        <TabsContent value="other" className="space-y-3 mt-4">
          {otherRequests.length === 0 
            ? renderEmptyState('No hay documentos en esta categoría.')
            : otherRequests.map(renderRequestCard)
          }
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MySignaturesPage;
